<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Components
	import ButtonType from './ButtonType.svelte';
	import ButtonModeOptionsOutput from './ButtonModeOptionsOutput.svelte';
	import InitialStateOptionsOutput from './InitialStateOptionsOutput.svelte';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../../services/gen_2_http_reqs';
	//Types
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Container, Select, toast } from '@Shelly/shelly-design-system';
	import { onMount } from 'svelte';

	export let device;

	const id = real_id(device.id);

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	let typeValue = settings?.[`input:${device.channel}`].type;
	let cacheTypeValue = typeValue;
	let modeValue = settings?.[`${get_gen_2_component_channel_structure(device)}`].in_mode;
	let cacheModeValue = modeValue;
	let initialStateValue = settings?.[`${get_gen_2_component_channel_structure(device)}`].initial_state;
	let cacheInitialStateValue = initialStateValue;
	let loading = false;
	let channel;
	let channelValue;
	let inputsSettings = [];

	const options = ['button', 'switch'];
	const _options = [$format('working_type_button'), $format('working_type_switch')];
	const _modeValues = {
		follow: $format('btn_mode_follow_scc_toast', { default: 'follow' }),
		flip: $format('btn_mode_flip_scc_toast', { default: 'flip' }),
		detached: $format('btn_mode_detached_scc_toast', { default: 'detached' }),
		activate: $format('btn_mode_activate_scc_toast', { default: 'activate' }),
		dim: $format('btn_mode_dimming_scc_toast', { default: 'dim' }),
		dual_dim: $format('btn_mode_dual_dim_scc_toast', { default: 'dual dim' }),
	};
	$: buttonModeOptions = {
		follow: {
			type: 'switch',
			text: $format('btn_mode_follow_description'),
			title: $format('btn_mode_follow_title', { default: 'Toggle switch' }),
			description: $format('btn_mode_follow_desc', {
				default: 'Act as a flip switch with one state for "ON" and one state for "OFF".',
			}),
		},
		flip: {
			type: 'switch',
			text: $format('btn_mode_flip_description'),
			title: $format('btn_mode_flip_title', { default: 'Edge switch' }),
			description: $format('btn_mode_flip_desc', {
				default: 'Changes state on every hit.',
			}),
		},
		detached: {
			type: 'any',
			text: $format('btn_mode_detached_description'),
			title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
			description: $format('btn_mode_detached_desc', {
				default: 'Switch is separated from the relays.',
			}),
		},
		activate: {
			type: 'any',
			text: $format('btn_mode_activate_description', {
				default:
					'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
			}),
			title: $format('btn_mode_activate_title', { default: 'Activation switch' }),
			description: $format('btn_mode_activate_desc', {
				default: 'Used with motion sensor. Any input turns ON and resets Auto Off timer.',
			}),
		},
		dim: {
			type: 'button',
			text: $format('btn_mode_dimming_description', {
				default:
					'One button dimming control - Push to toggle, hold to dim up/down (changes direction on each hold)',
			}),
			title: $format('btn_mode_dimming_title', { default: 'One button dimming control' }),
			description: $format('btn_mode_dimming_desc', {
				default: 'Push to toggle, hold to dim up/down.',
			}),
		},
		dual_dim: {
			type: typeValue === 'switch' ? 'disable' : 'button',
			text: $format('btn_mode_dual_dim_description', {
				default: 'Dual button dim mode - 2 inputs configuration (buttons) for up/down',
			}),
			title: $format('btn_mode_dual_dim_title', { default: 'Dual button dim mode' }),
			description: $format('btn_mode_dual_dim_desc', {
				default: '2 inputs configuration (buttons) for up/down.',
			}),
		},
	};
	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description'),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description'),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description'),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
	};
	const _initialStateOptionsScc = {
		on: $format('init_state_on_scc_toast', { default: 'on' }),
		off: $format('init_state_off_scc_toast', { default: 'off' }),
		restore_last: $format('init_state_restore_last_scc_toast', {
			default: 'restore last',
		}),
	};

	const setModeOptions = async () => {
		loading = true;
		if (modeValue === 'dual_dim') {
			const changeInput1: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
				id: 1,
				config: { type: typeValue },
			});
			if (changeInput1.isok) {
				settings[`input:1`].type = typeValue;
				devices_settings.update_devices_settings(id, settings);
			}
		}
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: channel,
				config: { in_mode: modeValue },
			},
		);

		if (res.isok) {
			settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('btn_mode_scc_toast', {
					values: { mode_value: _modeValues[modeValue] },
				}),
			});
			cacheModeValue = modeValue;
		} else {
			modeValue = cacheModeValue;
			toast.open({ type: 'danger', title: $format('btn_mode_err_toast') });
		}
	};
	const setTypeValue = async () => {
		// set mutually exclusive settings on for other settings

		const res_input: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
			id: channel,
			config: { type: typeValue },
		});

		if (res_input.isok) {
			toast.open({
				type: 'success',
				title: $format('input_type_scc_toast', {
					values: { mode_value: _options[options.indexOf(typeValue)] },
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({ type: 'danger', title: $format('btn_type_err_toast') });
		}

		if (typeValue === 'button' && modeValue === 'dual_dim') {
			const changeInput1: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
				id: 1,
				config: { type: typeValue },
			});
			if (changeInput1.isok) {
				settings[`input:1`].type = typeValue;
				devices_settings.update_devices_settings(id, settings);
			}
		}
		if (
			buttonModeOptions[modeValue].type === typeValue ||
			buttonModeOptions[modeValue].type === 'any'
		) {
			return;
		} else {
			if (typeValue === 'button' && settings[`input:1`].type !== 'switch') {
				modeValue = 'dual_dim';
			} else if (typeValue === 'button' && settings[`input:1`].type === 'switch') {
				modeValue = 'dim';
			} else {
				modeValue = 'follow';
			}
		}
		if (channel === 0) {
			const res_switch: SettingRequest = await gen2Request(
				device,
				`${get_gen_2_component_from_category(device)}.SetConfig`,
				{
					id: channel,
					config: { in_mode: modeValue, initial_state: initialStateValue },
				},
			);

			if (res_switch.isok) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: _modeValues[modeValue] },
					}),
				});
				cacheModeValue = modeValue;
			} else {
				modeValue = cacheModeValue;
				toast.open({ type: 'danger', title: $format('btn_mode_err_toast') });
			}
		}
	};
	const setInitialStateValue = async () => {
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: channel,
				config: { initial_state: initialStateValue },
			},
		);

		if (res.isok) {
			settings[`${get_gen_2_component_channel_structure(device)}`].initial_state =
				initialStateValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('init_state_scc_toast', {
					values: {
						initial_state_value: _initialStateOptionsScc[initialStateValue],
					},
				}),
			});
			cacheInitialStateValue = initialStateValue;
		} else {
			initialStateValue = cacheInitialStateValue;
			toast.open({ type: 'danger', title: $format('init_state_err_toast') });
		}
	};
	const setButtonTypeInputs = async () => {
		debugger;
		const method = 'input.setconfig';
		const params = { id: channel, config: { type: typeValue } };

		const res: SettingRequest = await gen2Request(device, method, params);

		if (res.isok) {
			settings[`input:${channel}`].type = typeValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('btn_type_scc_toast', {
					values: { type_value: typeValue },
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({ type: 'danger', title: $format('btn_type_err_toast') });
		}
	};

	const sentRequests = async () => {
		if (device.category !== 'inputs_reader') {
			if (cacheTypeValue !== typeValue) {
				await setTypeValue();
			}
			if (cacheModeValue !== modeValue) {
				await setModeOptions();
			}
			if (cacheInitialStateValue !== initialStateValue) {
				await setInitialStateValue();
			}
			settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
			settings[`input:${channel}`].initial_state = initialStateValue;
			devices_settings.update_devices_settings(id, settings);
			loading = false;
		} else {
			if (cacheTypeValue !== typeValue) {
				await setButtonTypeInputs();
			}
		}
	};

	const getSettings = () => {
		channelValue = {
			title: channel,
			value: channel,
		};
		typeValue = settings[`input:${channel}`].type;
		cacheTypeValue = typeValue;
		modeValue = settings[`${get_gen_2_component_channel_structure(device)}`].in_mode;
		cacheModeValue = modeValue;
		initialStateValue = settings[`${get_gen_2_component_channel_structure(device)}`].initial_state;
		cacheInitialStateValue = initialStateValue;
	};

	const setChannel = (e) => {
		channel = typeof e.detail.value !== 'undefined' ? e.detail.value : device.channel;
		channelValue = {
			title: channel,
			value: channel,
		};
		getSettings();
	};

	onMount(() => {
		channel = device.channel;
		getSettings();
		Object.keys(settings).forEach((setting, i) => {
			if (setting.includes('input:') && Number(setting.split(':')[1]) < 100) {
				inputsSettings.push({
					title: +setting.split(':')[1],
					value: +setting.split(':')[1],
				});
			}
		});
		inputsSettings = inputsSettings.sort((a, b) => a.title - b.title);
	});
</script>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Select
			class="mb-m"
			icon="fas fa-hashtag"
			options={inputsSettings}
			value={channelValue}
			on:change={setChannel}
			id="select-9zlc32z"
			aria-label={$format('title_select_input', { default: 'Select input' })}
		/>
		<ButtonType bind:typeValue {options} {_options} />
		{#if channel === 0}
			<hr class="mb-m mt-s" />
			<ButtonModeOptionsOutput {typeValue} bind:modeValue {buttonModeOptions} />
			<InitialStateOptionsOutput {typeValue} bind:initialStateValue {initialStateOptions} />
		{/if}
	</Container>
	<div class="mb-s">
		<Button class="mt-l" {loading} size="m" width="full" on:click={sentRequests}>
			{$format('button_save')}
		</Button>
	</div>
{:else}
	<Select
		class="mb-m"
		icon="fas fa-hashtag"
		options={inputsSettings}
		value={channelValue}
		on:change={setChannel}
		id="select-9zlc32z"
		aria-label={$format('title_select_input', { default: 'Select input' })}
	/>
	<ButtonType bind:typeValue {options} {_options} />
	{#if channel === 0}
		<hr class="mb-m mt-s" />
		<ButtonModeOptionsOutput {typeValue} bind:modeValue {buttonModeOptions} />
		<InitialStateOptionsOutput {typeValue} bind:initialStateValue {initialStateOptions} />
	{/if}
	<Button {loading} size="m" width="full" on:click={sentRequests}>
		{$format('button_save')}
	</Button>
{/if}
